/**
 * #487 Holographic Text（全息文字）
 * 
 * 2026 设计趋势 - 全息投影效果
 * 
 * 核心特性：
 * - 全息文字（彩虹色折射 + 扫描线动画）
 * - 轻微抖动效果（模拟全息投影不稳定）
 * - 扫描线（模拟投影仪）
 * - 背景全息粒子
 * - Spring 物理动画
 * 
 * 应用场景：
 * - 科技产品发布
 * - 未来感品牌设计
 * - 科幻内容创作
 * - 小红书科技视频
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// ============= 主组件 =============
export const HolographicText: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // ============= 动画配置 =============
  
  // Spring 入场动画（平滑无弹跳）
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 200, stiffness: 100 },
  });

  const particleSpring = spring({
    frame,
    fps,
    config: { damping: 180, stiffness: 80 },
  });

  // 全息文字入场延迟
  const textDelay = 10;
  const textProgress = spring({
    frame: frame - textDelay,
    fps,
    config: { damping: 200, stiffness: 120 },
  });

  // ============= 全息效果参数 =============
  
  // 彩虹色相旋转（0-360 循环）
  const hueRotation = interpolate(
    frame % (fps * 3), // 3 秒循环
    [0, fps * 3],
    [0, 360]
  );

  // 扫描线位置（垂直移动）
  const scanlineY = interpolate(
    frame % (fps * 1.5), // 1.5 秒循环
    [0, fps * 1.5],
    [-height * 0.3, height * 1.3]
  );

  // 轻微抖动（模拟全息不稳定）
  const glitchOffsetX = Math.sin(frame * 0.5) * 2 + random('glitch-x') * 1.5;
  const glitchOffsetY = Math.cos(frame * 0.7) * 1.5 + random('glitch-y') * 1;

  // 闪烁效果（轻微）
  const flickerOpacity = interpolate(
    Math.sin(frame * 15),
    [-1, 1],
    [0.92, 1]
  );

  // 全息条纹动画
  const stripeOffset = interpolate(
    frame % (fps * 2), // 2 秒循环
    [0, fps * 2],
    [0, 100]
  );

  // ============= 背景粒子（30 个） =============
  const particles = Array.from({ length: 30 }, (_, i) => {
    const angle = (i / 30) * Math.PI * 2;
    const radius = 200 + random(`particle-${i}`) * 150;
    
    return {
      id: i,
      x: width / 2 + Math.cos(angle + frame * 0.02) * radius,
      y: height / 2 + Math.sin(angle + frame * 0.02) * radius,
      size: 2 + random(`size-${i}`) * 4,
      opacity: 0.3 + random(`opacity-${i}`) * 0.4,
      hue: (i * 12 + hueRotation) % 360, // 彩虹色分布
    };
  });

  // ============= 渲染 =============
  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(circle at 50% 50%, #1a1a2e 0%, #0f0f23 100%)',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* ============= 背景全息粒子 ============= */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: particleSpring,
        }}
      >
        {particles.map((particle) => (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              left: particle.x,
              top: particle.y,
              width: particle.size,
              height: particle.size,
              borderRadius: '50%',
              background: `hsl(${particle.hue}, 100%, 70%)`,
              opacity: particle.opacity * flickerOpacity,
              boxShadow: `0 0 ${particle.size * 2}px hsl(${particle.hue}, 100%, 70%)`,
              filter: 'blur(0.5px)',
            }}
          />
        ))}
      </div>

      {/* ============= 全息背景光效 ============= */}
      <div
        style={{
          position: 'absolute',
          width: width * 1.2,
          height: height * 0.8,
          background: `conic-gradient(
            from ${hueRotation}deg at 50% 50%,
            hsla(0, 100%, 60%, 0.1) 0deg,
            hsla(60, 100%, 60%, 0.1) 60deg,
            hsla(120, 100%, 60%, 0.1) 120deg,
            hsla(180, 100%, 60%, 0.1) 180deg,
            hsla(240, 100%, 60%, 0.1) 240deg,
            hsla(300, 100%, 60%, 0.1) 300deg,
            hsla(360, 100%, 60%, 0.1) 360deg
          )`,
          opacity: titleSpring * 0.6,
          filter: 'blur(60px)',
        }}
      />

      {/* ============= 扫描线效果 ============= */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: 3,
          top: scanlineY,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(0, 255, 255, 0.8) 50%, 
            transparent 100%
          )`,
          opacity: 0.6,
          boxShadow: '0 0 20px rgba(0, 255, 255, 0.8)',
        }}
      />

      {/* ============= 主文字容器 ============= */}
      <div
        style={{
          position: 'relative',
          transform: `
            scale(${textProgress})
            translate(${glitchOffsetX}px, ${glitchOffsetY}px)
          `,
          opacity: flickerOpacity,
        }}
      >
        {/* 文字阴影层（彩虹色） */}
        <div
          style={{
            position: 'absolute',
            fontSize: 100,
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            color: 'transparent',
            background: `linear-gradient(
              ${hueRotation}deg,
              hsl(0, 100%, 60%),
              hsl(60, 100%, 60%),
              hsl(120, 100%, 60%),
              hsl(180, 100%, 60%),
              hsl(240, 100%, 60%),
              hsl(300, 100%, 60%),
              hsl(360, 100%, 60%)
            )`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            textShadow: `
              0 0 20px rgba(0, 255, 255, 0.8),
              0 0 40px rgba(0, 255, 255, 0.6),
              0 0 60px rgba(255, 0, 255, 0.4)
            `,
            letterSpacing: 8,
            left: -4, // 偏移创建立体感
            top: -4,
          }}
        >
          HOLOGRAM
        </div>

        {/* 主文字层 */}
        <div
          style={{
            position: 'relative',
            fontSize: 100,
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            color: 'white',
            background: `linear-gradient(
              ${hueRotation + 180}deg,
              hsl(180, 100%, 70%),
              hsl(240, 100%, 70%),
              hsl(300, 100%, 70%),
              hsl(360, 100%, 70%),
              hsl(60, 100%, 70%)
            )`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            textShadow: `
              0 0 10px rgba(0, 255, 255, 0.6),
              0 0 20px rgba(0, 255, 255, 0.4),
              0 0 30px rgba(255, 0, 255, 0.3)
            `,
            letterSpacing: 8,
            // 全息条纹效果
            backgroundSize: '100% 200%',
            backgroundPosition: `0 ${stripeOffset}%`,
          }}
        >
          HOLOGRAM
        </div>

        {/* 全息条纹覆盖层 */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `repeating-linear-gradient(
              0deg,
              transparent 0px,
              transparent 3px,
              rgba(0, 255, 255, 0.1) 3px,
              rgba(0, 255, 255, 0.1) 4px
            )`,
            mixBlendMode: 'overlay',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* ============= 底部说明文字 ============= */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          fontSize: 24,
          color: 'rgba(0, 255, 255, 0.8)',
          fontFamily: 'Arial, sans-serif',
          opacity: spring({ frame: frame - 30, fps, config: { damping: 200 } }),
          textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
          letterSpacing: 2,
        }}
      >
        2026 Design Trend
      </div>

      {/* ============= 效果编号 ============= */}
      <div
        style={{
          position: 'absolute',
          bottom: 30,
          right: 30,
          fontSize: 16,
          color: 'rgba(255, 255, 255, 0.4)',
          fontFamily: 'monospace',
        }}
      >
        #487
      </div>
    </AbsoluteFill>
  );
};

// ============= Composition 配置 =============
export const HolographicTextComposition = () => {
  return <HolographicText />;
};
